/**
 * OraTopBar — thin top-bar "Ask ORA" text affordance, summonable only.
 *
 * Per the ESME × 21-ERT validation swarm: NO floating bubble. NO notification
 * dot. NO is-typing indicator. NO idle pop. ORA is the HOST, not the desk
 * clerk. Summonable only — never pops itself. First-impression discipline is
 * load-bearing for the three-character brand (Kim/Warren/ORA).
 *
 * Click → modal opens with:
 *   - 3 contextual recommendations (from /api/oracle/recommend, cached 10min)
 *   - For Pro+ users: a text input that hits /api/oracle/chat
 *   - For free users: trial CTA + static recs only (no chat input)
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send } from "lucide-react";
import api from "@/utils/api";
import OraStallTap from "@/components/OraStallTap";
import OraChurnSave from "@/components/OraChurnSave";

export default function OraTopBar({ theme }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* The affordance — thin gold-underlined link in the top bar */}
      <button
        onClick={() => setOpen(true)}
        data-testid="ora-topbar-trigger"
        style={{
          position: "fixed",
          top: 12,
          right: 16,
          zIndex: 40,
          background: "transparent",
          color: theme?.primary || "#C9A227",
          border: "none",
          fontSize: "0.78rem",
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontFamily: "Space Grotesk, sans-serif",
          cursor: "pointer",
          padding: "4px 8px",
          borderBottom: `1px solid ${theme?.primary || "#C9A227"}55`,
        }}
      >
        Ask ORA
      </button>

      {open && <OraDrawer theme={theme} onClose={() => setOpen(false)} />}
    </>
  );
}

function OraDrawer({ theme, onClose }) {
  const [recs, setRecs] = useState(null);
  const [stallTap, setStallTap] = useState(null);
  const [churnSave, setChurnSave] = useState(null);
  const [message, setMessage] = useState("");
  const [chatResponse, setChatResponse] = useState(null);
  const [tierGated, setTierGated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .post('/oracle/recommend', { surface: "top_bar" })
      .then((r) => {
        setRecs(r.data?.recommendations || null);
        // v1.3: stall_tap is null when no tap is due, dict when one fires.
        setStallTap(r.data?.stall_tap || null);
        // v1.4: churn_save_offer takes priority over stall_tap when both apply
        // (backend ensures they're mutually exclusive, but defensive here).
        setChurnSave(r.data?.churn_save_offer || null);
        // Log shown for the primary rec.
        // v1.1: echo state + profile so the backend aggregator can bucket events.
        const primary = r.data?.recommendations?.primary;
        const state = r.data?.recommendations?.state || r.data?.state;
        const profile = r.data?.recommendations?.profile || r.data?.profile;
        if (primary) {
          api.post('/oracle/telemetry', {
            event: "shown",
            rec_template_id: primary.id,
            rec_kind: primary.kind,
            promised_outcome: primary.promised_outcome,
            surface: "top_bar",
            state,
            profile,
          }).catch(() => {});
        }
      })
      .catch(() => { /* drawer still works without recs */ })
      .finally(() => setLoading(false));
  }, []);

  async function handleSend(e) {
    e?.preventDefault?.();
    const msg = message.trim();
    if (!msg || sending) return;
    setSending(true);
    setChatResponse(null);
    try {
      const r = await api.post('/oracle/chat', { message: msg });
      setChatResponse(r.data?.response || "(no response)");
      if (r.data?.tier_gated) setTierGated(true);
    } catch (e) {
      setChatResponse("ORA hit a snag. Try again, or click a recommendation below.");
    } finally {
      setSending(false);
    }
  }

  function handleRecClick(rec) {
    if (!rec) return;
    api.post('/oracle/telemetry', {
      event: "clicked",
      rec_template_id: rec.id,
    }).catch(() => {});
    onClose();
    if (rec.cta_path) navigate(rec.cta_path);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        paddingTop: 56, paddingLeft: 16, paddingRight: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 540,
          background: theme?.cardBg || "#0A0A0B",
          border: `1px solid ${theme?.primary || "#C9A227"}40`,
          borderRadius: 14,
          padding: "20px 22px",
          boxShadow: `0 16px 60px rgba(0,0,0,0.6)`,
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: "0.65rem", fontWeight: 800, letterSpacing: "0.22em", color: theme?.primary || "#C9A227" }}>
              ORA · YOUR HOST
            </div>
            <div style={{ fontSize: "0.75rem", color: theme?.textSecondary, marginTop: 2 }}>
              Navigation · features · trial questions. For market opinions on tickers, ask Warren.
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            background: "transparent", border: "none", color: theme?.textSecondary, cursor: "pointer", padding: 4,
          }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* v1.4: Churn save has highest priority — surfaces above stall + recs.
            Backend guarantees only one of churn_save or stall_tap is set per
            response, but we defensively render churn first if both ever land. */}
        {!loading && churnSave && <OraChurnSave offer={churnSave} theme={theme} />}

        {/* v1.3: Stall tap renders ABOVE the recommendations — heavier visual
            weight signals "I noticed you were gone" without saying it. */}
        {!loading && !churnSave && stallTap && <OraStallTap tap={stallTap} theme={theme} />}

        {/* Recommendations */}
        {!loading && recs && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: chatResponse || tierGated ? 14 : 0 }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.14em", color: theme?.textSecondary, textTransform: "uppercase", marginBottom: 4 }}>
              What I'd suggest next
            </div>
            {[recs.primary, recs.secondary, recs.tertiary].filter(Boolean).map((rec, i) => (
              <button
                key={rec.id}
                onClick={() => handleRecClick(rec)}
                data-testid={`ora-drawer-rec-${rec.id}`}
                style={{
                  textAlign: "left", cursor: "pointer",
                  background: i === 0
                    ? `linear-gradient(135deg, ${theme?.primary || "#C9A227"}18, ${theme?.accent || "#E6C04A"}08)`
                    : "transparent",
                  border: `1px solid ${i === 0 ? (theme?.primary || "#C9A227") + "55" : theme?.borderColor || "#333"}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  color: theme?.textPrimary || "#F5F2E9",
                  fontSize: "0.85rem", fontWeight: 600,
                }}
              >
                <div>{rec.label}</div>
                <div style={{ fontSize: "0.72rem", fontWeight: 400, color: theme?.textSecondary, marginTop: 2 }}>{rec.body}</div>
              </button>
            ))}
          </div>
        )}

        {/* Chat input (Pro+ only) */}
        <form onSubmit={handleSend} style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="ora-drawer-input"
            placeholder="Ask ORA — 'how do I…', 'where is…', 'what does X mean'"
            disabled={sending}
            style={{
              flex: 1,
              background: theme?.background || "#14130F",
              color: theme?.textPrimary || "#F5F2E9",
              border: `1px solid ${theme?.borderColor || "#333"}`,
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: "0.85rem",
              fontFamily: "inherit",
            }}
          />
          <button
            type="submit"
            disabled={sending || !message.trim()}
            data-testid="ora-drawer-send"
            style={{
              background: `linear-gradient(135deg, ${theme?.primary || "#C9A227"}, ${theme?.accent || "#E6C04A"})`,
              color: "#0A0A0B", border: "none",
              borderRadius: 10, padding: "10px 14px",
              cursor: sending || !message.trim() ? "not-allowed" : "pointer",
              opacity: sending || !message.trim() ? 0.5 : 1,
            }}
          >
            <Send className="w-4 h-4" />
          </button>
        </form>

        {/* Chat response */}
        {chatResponse && (
          <div
            data-testid="ora-drawer-response"
            style={{
              marginTop: 12,
              padding: "12px 14px",
              background: `${theme?.primary || "#C9A227"}08`,
              border: `1px solid ${theme?.borderColor || "#333"}`,
              borderRadius: 10,
              color: theme?.textPrimary || "#F5F2E9",
              fontSize: "0.86rem",
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
            }}
          >
            {chatResponse}
            {tierGated && (
              <button
                onClick={() => { onClose(); navigate("/subscribe"); }}
                style={{
                  marginTop: 10, display: "block", width: "100%",
                  background: `linear-gradient(135deg, ${theme?.primary || "#C9A227"}, ${theme?.accent || "#E6C04A"})`,
                  color: "#0A0A0B", border: "none", borderRadius: 8, padding: "8px 12px",
                  fontWeight: 700, cursor: "pointer", fontSize: "0.8rem",
                }}
              >
                Start 7-day Pro trial
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
